import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, ShieldCheck, FileText, Upload, Plus, Trash2, Edit3, Eye,
  ArrowLeft, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DESIGNATIONS = ['Chairman', 'Secretary', 'Treasurer', 'Committee Member', 'Committee Member'];

export default function CommitteeManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showObserver, setShowObserver] = useState(false);
  const [showVisibility, setShowVisibility] = useState(false);
  const [showObsReset, setShowObsReset] = useState(false);
  const [showResolution, setShowResolution] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/buyer/committee/config`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load committee configuration');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const savePolicy = async (total, min) => {
    setBusy(true);
    try {
      await axios.put(`${API}/buyer/committee/config`, { total_members: total, min_approvals: min }, { withCredentials: true });
      toast.success('Approval policy saved');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this committee member? Their account will be disabled.')) return;
    try {
      await axios.delete(`${API}/buyer/committee/members/${id}`, { withCredentials: true });
      toast.success('Member removed');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeObserver = async () => {
    if (!window.confirm('Remove Observer account?')) return;
    try {
      await axios.delete(`${API}/buyer/committee/observer`, { withCredentials: true });
      toast.success('Observer removed');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-navy" /></div>;
  const cfg = data?.config || {};
  const members = data?.members || [];
  const observer = data?.observer;
  const activeRes = data?.active_resolution;
  const resolutions = data?.resolutions || [];
  const maxV1 = data?.max_members_v1 || 5;

  const policyOk = cfg.total_members >= 2 && cfg.min_approvals >= 1 && cfg.min_approvals <= cfg.total_members;
  const membersOk = members.length >= (cfg.total_members || 0) && cfg.total_members > 0;
  const workflowEnabled = policyOk && membersOk && !!activeRes;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8" data-testid="committee-mgmt-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/buyer-dashboard')} className="text-sm text-navy font-semibold mb-4 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-black text-navy">Committee Management</h1>
            <p className="text-sm text-slate-600 mt-1">Configure your Managing Committee for procurement approvals.</p>
          </div>
          <StatusBadge enabled={workflowEnabled} />
        </div>

        {/* Approval Policy */}
        <Card title="Approval Policy" icon={ShieldCheck} testid="policy-card">
          <PolicyForm cfg={cfg} maxV1={maxV1} onSave={savePolicy} busy={busy} />
        </Card>

        {/* Members */}
        <Card title={`Committee Members (${members.length} / ${maxV1})`} icon={Users} testid="members-card"
              headerAction={members.length < maxV1 && <button data-testid="add-member-btn" onClick={() => setShowAdd(true)} className="text-xs px-3 py-1.5 rounded bg-navy text-white font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Member</button>}>
          {members.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No members added yet. Add up to {maxV1}. Members will choose their own username &amp; password on first login.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr><th className="text-left py-2">#</th><th className="text-left">Name</th><th className="text-left">Designation</th><th className="text-left">Email</th><th className="text-left">Username</th><th className="text-left">Status</th><th></th></tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} className="border-b" data-testid={`member-row-${m.display_order}`}>
                    <td className="py-3 font-bold">{m.display_order}</td>
                    <td>{m.name}</td>
                    <td className="text-slate-700">{m.designation}</td>
                    <td className="text-slate-600 text-xs">{m.email}</td>
                    <td className="text-slate-800 text-xs font-mono" data-testid={`member-username-${m.display_order}`}>{m.username || <span className="text-slate-400">—</span>}</td>
                    <td><StatusPill status={m.account_status} /></td>
                    <td className="text-right"><button data-testid={`del-member-${m.id}`} onClick={() => removeMember(m.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
            <strong>Privacy:</strong> Members choose their own username &amp; password on first login. You (Buyer Admin) can see the username and account status here, but the password is <strong>never</strong> visible to anyone else.
          </div>
        </Card>

        {/* Observer */}
        <Card title="Society Observer (View Only)" icon={Eye} testid="observer-card"
              headerAction={!observer && <button data-testid="add-observer-btn" onClick={() => setShowObserver(true)} className="text-xs px-3 py-1.5 rounded bg-navy text-white font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Observer</button>}>
          {observer ? (
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-slate-500">Username</div>
                  <div className="font-bold text-navy text-lg font-mono" data-testid="observer-username">{observer.username || '—'}</div>
                  <div className="text-xs text-slate-500 mt-2">Visibility: <strong className="text-navy" data-testid="observer-visibility-mode">{observer.visibility_mode === 'all' ? 'All Live Procurements' : `Selected (${observer.selected_tender_ids?.length || 0})`}</strong></div>
                </div>
                <div className="flex flex-col gap-1">
                  <button data-testid="edit-visibility-btn" onClick={() => setShowVisibility(true)} className="text-xs px-3 py-1.5 rounded border border-navy/40 text-navy hover:bg-navy/5">Manage Visibility</button>
                  <button data-testid="reset-obs-pw-btn" onClick={() => setShowObsReset(true)} className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">Reset Password</button>
                  <button data-testid="remove-observer-btn" onClick={removeObserver} className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50">Remove</button>
                </div>
              </div>
              <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
                <strong>Note:</strong> Observer credentials (username &amp; password) are managed <em>only</em> by you. The Observer cannot change them, and cannot alter visibility settings.
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4">No Observer. Create one View-Only account so ordinary society members can transparently monitor procurement.</p>
          )}
        </Card>

        {/* Resolution */}
        <Card title="Society Committee Resolution" icon={FileText} testid="resolution-card"
              headerAction={<button data-testid="upload-resolution-btn" onClick={() => setShowResolution(true)} className="text-xs px-3 py-1.5 rounded bg-navy text-white font-bold flex items-center gap-1"><Upload className="w-3 h-3" /> {activeRes ? 'Upload New Version' : 'Upload Resolution'}</button>}>
          {activeRes ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-bold text-navy">Active: v{activeRes.version} · {activeRes.resolution_number}</span>
              </div>
              <div className="text-xs text-slate-600">
                Resolution Date: {activeRes.resolution_date} · Effective: {activeRes.effective_date} · Uploaded by {activeRes.uploaded_by_name} on {new Date(activeRes.uploaded_at).toLocaleDateString()}
              </div>
              {resolutions.length > 1 && (
                <details className="mt-3">
                  <summary className="text-xs text-navy font-semibold cursor-pointer">Version History ({resolutions.length})</summary>
                  <ul className="mt-2 space-y-1 text-xs text-slate-600">
                    {resolutions.map(r => (
                      <li key={r.id} className={r.id === activeRes.id ? 'text-navy font-bold' : ''}>
                        v{r.version} · {r.resolution_number} · {r.resolution_date} {r.superseded_at ? '(superseded)' : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4">Upload the Society Committee Resolution PDF to enable the digital approval workflow.</p>
          )}
        </Card>

        {/* Status Panel */}
        <Card title="Workflow Status" icon={AlertCircle} testid="status-card">
          <ul className="text-sm space-y-2">
            <StatusLine ok={policyOk} label={`Approval policy configured (${cfg.min_approvals || 0} of ${cfg.total_members || 0})`} />
            <StatusLine ok={membersOk} label={`${members.length} of ${cfg.total_members || 0} member accounts created`} />
            <StatusLine ok={!!activeRes} label="Society Resolution uploaded" />
          </ul>
          {workflowEnabled ? (
            <div className="mt-4 p-3 rounded bg-green-50 border border-green-200 text-sm text-green-800" data-testid="workflow-enabled-msg">
              <strong>Committee Approval Workflow is ACTIVE.</strong> Manager Recommendations from tenders will route through this committee.
            </div>
          ) : (
            <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="workflow-disabled-msg">
              Complete all three steps above to activate the workflow.
            </div>
          )}
        </Card>
      </div>

      {showAdd && <AddMemberModal existingCount={members.length} maxV1={maxV1} usedOrders={members.map(m => m.display_order)} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); refresh(); }} />}
      {showObserver && <ObserverModal onClose={() => setShowObserver(false)} onSaved={() => { setShowObserver(false); refresh(); }} />}
      {showVisibility && observer && <VisibilityModal observer={observer} onClose={() => setShowVisibility(false)} onSaved={() => { setShowVisibility(false); refresh(); }} />}
      {showObsReset && <ObserverResetModal onClose={() => setShowObsReset(false)} onSaved={() => { setShowObsReset(false); }} />}
      {showResolution && <ResolutionModal onClose={() => setShowResolution(false)} onSaved={() => { setShowResolution(false); refresh(); }} />}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { cls: 'bg-green-100 text-green-800', label: 'Active' },
    pending_activation: { cls: 'bg-amber-100 text-amber-800', label: 'Pending Activation' },
    deleted: { cls: 'bg-red-100 text-red-800', label: 'Removed' },
  };
  const s = map[status] || map.pending_activation;
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${s.cls}`}>{s.label}</span>;
}

function VisibilityModal({ observer, onClose, onSaved }) {
  const [mode, setMode] = useState(observer.visibility_mode || 'selected');
  const [tenders, setTenders] = useState([]);
  const [selected, setSelected] = useState(new Set(observer.selected_tender_ids || []));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/buyer/committee/tenders-for-visibility`, { withCredentials: true })
      .then(r => setTenders(r.data.tenders || []))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load tenders'));
  }, []);
  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.patch(`${API}/buyer/committee/observer/visibility`, {
        visibility_mode: mode,
        selected_tender_ids: mode === 'selected' ? Array.from(selected) : [],
      }, { withCredentials: true });
      toast.success('Observer visibility updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Manage Observer Visibility" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="visibility-form">
        <div className="text-xs text-slate-600">Only you (Buyer Admin) control what the Observer sees. The Observer has no visibility settings on their side.</div>
        <label className="flex items-start gap-2 p-3 rounded border cursor-pointer" data-testid="visibility-all-option">
          <input type="radio" checked={mode === 'all'} onChange={() => setMode('all')} className="mt-0.5" />
          <div>
            <div className="font-bold text-sm text-navy">Show All Live RFQs / Tenders</div>
            <div className="text-xs text-slate-500">Every live procurement will be visible to the Observer.</div>
          </div>
        </label>
        <label className="flex items-start gap-2 p-3 rounded border cursor-pointer" data-testid="visibility-selected-option">
          <input type="radio" checked={mode === 'selected'} onChange={() => setMode('selected')} className="mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-sm text-navy">Selected RFQs / Tenders Only <span className="text-[10px] font-normal text-slate-500">(default)</span></div>
            <div className="text-xs text-slate-500">Pick specific procurements to share below.</div>
          </div>
        </label>
        {mode === 'selected' && (
          <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1" data-testid="tender-picker">
            {tenders.length === 0 ? <div className="text-xs text-slate-500 p-2">No live tenders yet.</div> :
              tenders.map(t => (
                <label key={t.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`tender-pick-${t.id}`}>
                  <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
                  <span className="flex-1"><strong>{t.title}</strong> <span className="text-xs text-slate-500">· {t.work_category} · {t.status}</span></span>
                </label>
              ))
            }
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-slate-200 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50" data-testid="submit-visibility-btn">{saving ? 'Saving…' : 'Save Visibility'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ObserverResetModal({ onClose, onSaved }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setSaving(true);
    try {
      await axios.post(`${API}/buyer/committee/observer/reset-password`, { password: pw }, { withCredentials: true });
      toast.success('Observer password reset. Share the new password securely.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Reset Observer Password" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="reset-obs-form">
        <div className="text-xs text-slate-600">Set a new password for the Observer. They cannot change it themselves.</div>
        <Input label="New Password" testid="new-obs-pw" type="password" value={pw} onChange={setPw} required />
        <Input label="Confirm Password" testid="new-obs-pw2" type="password" value={pw2} onChange={setPw2} required />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-slate-200 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50" data-testid="submit-obs-reset-btn">{saving ? 'Saving…' : 'Reset Password'}</button>
        </div>
      </form>
    </Modal>
  );
}

function Card({ title, icon: Icon, children, testid, headerAction }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-gold" /><h2 className="text-lg font-bold text-navy">{title}</h2></div>
        {headerAction}
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ enabled }) {
  return enabled ? (
    <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-bold flex items-center gap-1" data-testid="workflow-status-badge"><CheckCircle2 className="w-3 h-3" /> ACTIVE</span>
  ) : (
    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center gap-1" data-testid="workflow-status-badge"><AlertCircle className="w-3 h-3" /> SETUP INCOMPLETE</span>
  );
}

function StatusLine({ ok, label }) {
  return <li className="flex items-center gap-2">{ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}<span className={ok ? 'text-slate-700' : 'text-slate-500'}>{label}</span></li>;
}

function PolicyForm({ cfg, maxV1, onSave, busy }) {
  const [total, setTotal] = useState(cfg.total_members || 3);
  const [min, setMin] = useState(cfg.min_approvals || 2);
  useEffect(() => { setTotal(cfg.total_members || 3); setMin(cfg.min_approvals || 2); }, [cfg.total_members, cfg.min_approvals]);
  const minOptions = Array.from({ length: total }, (_, i) => i + 1);
  return (
    <div className="grid sm:grid-cols-3 gap-4 items-end">
      <div>
        <label className="text-xs font-bold text-slate-500 uppercase">Total Committee Members</label>
        <select value={total} onChange={e => { const t = Number(e.target.value); setTotal(t); if (min > t) setMin(t); }} className="mt-1 w-full px-3 py-2 rounded border border-slate-200" data-testid="policy-total">
          {[2, 3, 4, 5].filter(n => n <= maxV1).map(n => <option key={n} value={n}>{n} Members</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-bold text-slate-500 uppercase">Minimum Approvals Required</label>
        <select value={min} onChange={e => setMin(Number(e.target.value))} className="mt-1 w-full px-3 py-2 rounded border border-slate-200" data-testid="policy-min">
          {minOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <button onClick={() => onSave(total, min)} disabled={busy} className="px-4 py-2 rounded bg-navy text-white font-bold disabled:opacity-50" data-testid="save-policy-btn">Save Policy</button>
    </div>
  );
}

function AddMemberModal({ existingCount, maxV1, usedOrders, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', designation: DESIGNATIONS[existingCount] || 'Committee Member', email: '', mobile: '', display_order: existingCount + 1 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/buyer/committee/members`, form, { withCredentials: true });
      toast.success('Member added. Invitation email sent.');
      if (data.invite_link) {
        // Fallback: copy invite link
        try { await navigator.clipboard.writeText(data.invite_link); } catch (e2) { /* clipboard unavailable */ }
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add member');
    } finally { setSaving(false); }
  };
  return (
    <Modal title="Add Committee Member" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="add-member-form">
        <Input label="Name" testid="member-name" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">Designation</label>
          <input list="designation-list" className="mt-1 w-full px-3 py-2 rounded border border-slate-200" data-testid="member-designation" value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} required />
          <datalist id="designation-list">
            {['Chairman', 'Secretary', 'Treasurer', 'Committee Member'].map(d => <option key={d} value={d} />)}
          </datalist>
        </div>
        <Input label="Email" testid="member-email" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} required />
        <Input label="Mobile" testid="member-mobile" value={form.mobile} onChange={v => setForm({ ...form, mobile: v })} required />
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">Display Order (1–{maxV1})</label>
          <select value={form.display_order} onChange={e => setForm({ ...form, display_order: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded border border-slate-200" data-testid="member-display-order">
            {Array.from({ length: maxV1 }, (_, i) => i + 1).map(n => (
              <option key={n} value={n} disabled={usedOrders.includes(n)}>{n} {usedOrders.includes(n) ? '(used)' : ''}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-slate-200 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50" data-testid="submit-member-btn">{saving ? 'Adding…' : 'Add Member & Send Invite'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ObserverModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/buyer/committee/observer`, form, { withCredentials: true });
      toast.success('Observer created. Share the username & password with them.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Create Observer Account" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="add-observer-form">
        <Input label="Username" testid="obs-username" value={form.username} onChange={v => setForm({ ...form, username: v.replace(/\s/g, '') })} required />
        <Input label="Password (min 8 chars)" testid="obs-password" type="password" value={form.password} onChange={v => setForm({ ...form, password: v })} required />
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
          Observer has view-only access. They cannot approve, reject, modify, or change their own username/password.
          Only you (Buyer Admin) can reset their credentials.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-slate-200 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50" data-testid="submit-observer-btn">{saving ? 'Creating…' : 'Create Observer Account'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ResolutionModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ resolution_number: '', resolution_date: '', effective_date: '', file_url: '', file_name: '', file_size: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/uploads/document?purpose=committee_resolution`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, file_url: data.url, file_name: file.name, file_size: file.size }));
      toast.success('Resolution PDF uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_url) return toast.error('Upload the resolution PDF first');
    setSaving(true);
    try {
      await axios.post(`${API}/buyer/committee/resolutions`, form, { withCredentials: true });
      toast.success('Resolution uploaded');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Upload Society Committee Resolution" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="upload-resolution-form">
        <Input label="Resolution Number" testid="res-number" value={form.resolution_number} onChange={v => setForm({ ...form, resolution_number: v })} required />
        <Input label="Resolution Date" testid="res-date" type="date" value={form.resolution_date} onChange={v => setForm({ ...form, resolution_date: v })} required />
        <Input label="Effective Date" testid="res-effective" type="date" value={form.effective_date} onChange={v => setForm({ ...form, effective_date: v })} required />
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">Resolution PDF</label>
          <input type="file" accept="application/pdf" onChange={e => handleFile(e.target.files?.[0])} className="mt-1 block w-full text-sm" data-testid="res-file" />
          {uploading && <div className="text-xs text-navy mt-1">Uploading…</div>}
          {form.file_url && <div className="text-xs text-green-700 mt-1">✓ {form.file_name}</div>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-slate-200 text-sm">Cancel</button>
          <button type="submit" disabled={saving || uploading || !form.file_url} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50" data-testid="submit-resolution-btn">{saving ? 'Saving…' : 'Save Resolution'}</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold text-navy">{title}</h3><button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button></div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-500 uppercase">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} className="mt-1 w-full px-3 py-2 rounded border border-slate-200 text-sm" data-testid={testid} />
    </div>
  );
}
